import type { EmailSnippetInput } from "@/schema/email-snippet"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { createEmailSnippet, destroyEmailSnippet, updateEmailSnippet } from "./api"

export function useCreateEmailSnippet() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ emailSnippet, scope }: { emailSnippet: EmailSnippetInput; scope?: "company" | "book" }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createEmailSnippet(context, emailSnippet, scope ? { scope } : undefined)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-snippets"] })
    },
  })
}

export function useUpdateEmailSnippet() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, emailSnippet }: { id: string; emailSnippet: Partial<EmailSnippetInput> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateEmailSnippet(context, id, emailSnippet)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-snippets"] })
    },
  })
}

export function useDestroyEmailSnippet() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return destroyEmailSnippet(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-snippets"] })
    },
  })
}

export const useDeleteEmailSnippet = useDestroyEmailSnippet
